/**
 * Types used by the study level DE/QA forms
 */

import { UseFormReturn } from 'react-hook-form';

export type ReviewReference = {
  id: number;
  primary_document_id: number | null;
  primary_document_extractable: boolean;
};

export enum ExtractionPermissions {
  CONFIGURE = 'can_configure_extraction',
  EDIT_TEMPLATE = 'can_edit_template',
}

export type UserPermissions = {
  canEditTemplate: boolean;
  canConfigure: boolean;
};

export type Arm = {
  id: string;
  intervention_id: string;
  intervention_group?: string;
  intervention_name?: string;
  intervention_group_suggestion_selected?: string;
  position: number;
};
export type UpdateArm = {
  intervention_group?: string;
  intervention_group_suggestion_selected?: string;
};
export type Intervention = {
  id: string;
  name: string;
  hasRequestPending?: boolean;
};

export type InterventionCharacteristicsResult = {
  arm_id: string;
  value: string;
};

export type InterventionCharacteristics = {
  id: string;
  name: string;
  results: InterventionCharacteristicsResult[];
};

export type TemplateOutcome = {
  id: string;
  name: string;
};
export type OutcomeDetail = {
  id?: string;
  name: string;
};

export type Reviewer = {
  id: number;
  first_name: string;
  last_name: string;
  name?: string;
  // Derived LibKey state for the current reviewer, served by the
  // current_reviewer endpoint. Absent on the lightweight {id, name} reviewer
  // shapes used elsewhere (e.g. reviewer pickers).
  libkey_enabled?: boolean;
  libkey_nomad_migration_eligible?: boolean;
  libkey_open_access_library?: boolean;
};

export type Identification = {
  id: number;
  address: string | null;
  authors_name: string | null;
  comments: string | null;
  country: string | null;
  email: string | null;
  institution: string | null;
  setting: string | null;
  sponsorship_source: string | null;
};

export type Methods = {
  id: number;
  design: string | null;
  group: string | null;
};

export type Participants = {
  id: number;
  inclusion_criteria: string | null;
  exclusion_criteria: string | null;
};

export type Pretreatments = {
  id: number;
  group_differences: string | null;
};

export type CustomFieldCategory =
  | 'identification'
  | 'population'
  | 'study_design';

export type CustomField = {
  id: number;
  category: CustomFieldCategory;
  name: string;
  value: string | null;
  ai_suggestion_alias?: string | null;
};

export type CustomFields = Record<CustomFieldCategory, CustomField[]>;

export enum HiddenFieldCategory {
  IDENTIFICATION = 'identification',
  METHODS = 'methods',
  POPULATION = 'population',
  OUTCOMES = 'outcomes',
}

export type HiddenFields = Record<HiddenFieldCategory, string[]>;

export type TemplateHiddenField = {
  id?: number;
  category: HiddenFieldCategory;
  name: string;
};

export type TemplateHiddenFields = TemplateHiddenField[];

// DE2 naming convention as it doesn't have the concept of custom fields
export type FieldSuggestionAlias = {
  field: string;
  aliases: string[];
  categories: CustomFieldCategory[];
};

// DE1 naming convention for the same payload
export type CustomFieldSupportedSuggestion = FieldSuggestionAlias;

export type SuggestibleBlockType =
  | 'TextField'
  | 'MultipleChoice'
  | 'Checkboxes';

// snake_case: nested keys arrive as Ruby sends them through react_component
// (autoMounter only camelises the top-level prop names), like
// `ai_suggestion_alias` and `custom_field_suggestions`
export type DE2SupportedSuggestion = FieldSuggestionAlias & {
  template_label: string;
  block_types: SuggestibleBlockType[];
};

export type NoteCategory =
  | 'identification'
  | 'study_design'
  | 'population'
  | 'interventions'
  | 'outcomes'
  | 'results data';

export type Note = {
  id: number;
  note: string;
  category: string;
  reviewer_id: number;
  review_study_id: number;
};

type Notes = Partial<Record<NoteCategory, Note>>;

export type DataExtractionContextDEMetadata = {
  primary_reviewer: Reviewer | null;
  permissions: ExtractionPermissions[];
  userPermissions: UserPermissions;
  doi: string | null;
  studyId: number;
  reviewReferences: ReviewReference[];
};

type DataExtractionReviewFeaturesMetadata = {
  hasFeature: (feature: string) => boolean;
  useSuggestions: boolean;
  useSuggestionsForDoi: boolean;
  // True until the review features fetch settles `useSuggestions`.
  featuresLoading: boolean;
};

export type DataExtractionContextProps = DataExtractionContextDEMetadata &
  DataExtractionReviewFeaturesMetadata;

export type DataExtraction = {
  primary_reviewer: Reviewer | null;
  permissions: ExtractionPermissions[];
  interventions: Intervention[];
  arms: Arm[];
  intervention_characteristics: InterventionCharacteristics[];
  template_outcomes: TemplateOutcome[];
  outcomes: Outcome[];
  custom_fields: CustomFields;
  hidden_fields: HiddenFields;
  identification: Identification | null;
  methods: Methods | null;
  pretreatments: Pretreatments | null;
  participants: Participants | null;
  baseline_characteristics: BaselineCharacteristic[];
  doi: string | null;
  notes: Notes;
  outcome_groups: OutcomeGroup[];
  result_types: ResultType[];
  template_outcome_details: OutcomeDetail[];
  review_references: ReviewReference[];
};
export type DataExtractionForm = Omit<
  DataExtraction,
  'primary_reviewer' | 'permissions' | 'doi'
>;

/**
 * Types used by the the DE/QA template pages
 */
export type DataExtractionTemplate = {
  identification: IdentificationData;
  methods: MethodsData;
  population: PopulationData;
  interventions: InterventionsData;
  outcomes: OutcomeData;
  consolidated_measures: ConsolidatedMeasure[];
  consolidated_measures_labels_modified: boolean;
  has_extracted_data: boolean;
  number_of_revisitable_studies: number;
  hidden_fields: TemplateHiddenFields;
  template_outcome_details: OutcomeDetail[];
  updated_by?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  updated_at?: string;
  custom_field_suggestions?: CustomFieldSupportedSuggestion[];
};

export type DataExtractionTemplateContext = UseFormReturn<
  DataExtractionTemplate,
  any,
  undefined
>;

export enum QualityAssessmentTemplateFormat {
  EMPTY = 'empty',
  ROB = 'rob',
}

export type QualityAssessmentTemplate = {
  domains: Domain[];
  outcome_groups: OutcomeGroup[];
  updated_by?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  updated_at?: string;
  number_of_revisitable_studies: number;
};

export type IdentificationData = {
  additionalFields: AdditionalField[];
};
export type MethodsData = {
  additionalFields: AdditionalField[];
};

export type PopulationData = {
  baseline_characteristics: BaselineCharacteristic[];
  additionalFields: AdditionalField[];
};

export type InterventionsData = {
  interventions: InterventionField[];
  characteristics: InterventionField[];
};

export type OutcomeData = {
  outcomes: DataExtractionTemplateOutcome[];
  default_continuous_measure: string;
  default_dichotomous_measure: string;
  data_types: DataType[];
  outcome_details: OutcomeDetail[];
};

/**
 * Fields
 */

export type AdditionalField = {
  id?: number;
  name: string;
};

export type InterventionField = {
  id?: number;
  name: string;
  position?: number;
};

export type BaselineCharacteristicResult = {
  // arm_id is null for the "Overall" column
  arm_id: string | null;
  value: string;
};

export type BaselineCharacteristic = {
  id?: number;
  name: string;
  results?: BaselineCharacteristicResult[];
};

export type DataExtractionTemplateTimepoint = {
  id?: string;
  name: string;
  position?: number;
};

export type Timepoint = {
  id: string;
  name: string;
  template_field_id?: string;
  reported_time?: string;
  remove?: () => Promise<{ ok: boolean }>;
  checkTimepointExtractedStatus?: () => Promise<{
    ok?: boolean;
    extracted?: boolean;
  }>;
  updateReportedTime?: (reportedTime: string) => Promise<{ ok: boolean }>;
};

// Outcome from the DataExtraction template
export type DataExtractionTemplateOutcome = {
  name: string;
  timepoints: DataExtractionTemplateTimepoint[];
  outcome_type: OutcomeTypes;
  has_extracted_data: boolean;
};

// Outcome from the DataExtraction on the ReviewStudy
export type Outcome = {
  id: string;
  name: string;
  direction: string | null;
  range: string | null;
  scale: string | null;
  unit_of_measure: string | null;
  outcome_type: OutcomeTypes;
  data_value: string | null;
  outcome_group_id: string | null;
  outcome_results: OutcomeResult[];
  result_values: Record<
    Arm['id'],
    Record<Timepoint['id'], Record<OutcomeResult['id'], ResultValue>>
  >;
  notes: string | null;
  template_field_id: string;
  timepoints: Timepoint[];
  template_timepoints: DataExtractionTemplateTimepoint[];
  reported_name?: string | null;
  position?: string | null;
  outcome_detail_values: Record<string, OutcomeDetailValue>;
};

export type OutcomeResult = {
  id: string;
  result_type_id: string;
};

export type OutcomeResultPair = {
  lowerInput: OutcomeResult;
  upperInput: OutcomeResult;
};

export type Measure = OutcomeResult | OutcomeResultPair; // Measures can be for one OutcomeResult or two in the case of result types belonging to a set

export type ResultValue = {
  id: string;
  outcome_result_id: string;
  value: string;
  arm_id: string;
  timepoint_id: string;
};

export type OutcomeDetailValue = {
  template_field_id: string;
  value: string;
};

export type OutcomeGroup = {
  id?: number;
  name: string;
  prompt_before_deleting: boolean;
};

export type FieldType = 'text' | 'numeric' | 'percentage';

export type ResultType = {
  id: string;
  label: string;
  description: string;
  field_type: FieldType;
  outcome_type: string;
  position: number;
  decimals: number;
  set: Set | null;
};

export type DataExtractionTemplateResultType = {
  id?: number;
  label: string;
  outcome_type: string;
  used?: boolean;
};

export type ConsolidatedMeasure = {
  label: string;
  used: boolean;
  custom_result_types: DataExtractionTemplateResultType[];
};

export type Set = {
  id: string;
  description: string;
  label: string;
};

export type Domain = {
  id?: number;
  name: string;
  description: string;
  outcome_specific: boolean;
  prompt_before_deleting: boolean;
  judgements?: {
    id?: number;
    label: string;
    position: number;
  }[];
};

export type DataType = {
  name: string;
  description: string;
  outcome_type: OutcomeTypes;
};

/**
 * Enums
 */

export enum OutcomeTypes {
  CONTINUOUS = 'ContinuousOutcome',
  DICHOTOMOUS = 'DichotomousOutcome',
  QUALITATIVE = 'QualitativeOutcome',
}

export enum AdditionalFieldCategory {
  METHODS = 'study_design',
  IDENTIFICATION = 'identification',
  POPULATION = 'population',
}
